import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Lock, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { AuthShell, PrimaryButton, AUTH_SUBTLE, AUTH_LABEL, AUTH_BLUE } from './authShared';

const ICON = '#98A2B3';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
    }
  }, [token]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    try {
      await apiService.resetPassword(token, formData.password);
      setIsSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password. The link may have expired.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell backTo={null}>
        <div className="text-center">
          <AlertCircle className="w-14 h-14 mx-auto mb-4" style={{ color: '#DC2626' }} />
          <h1 className="text-2xl mb-2">Invalid reset link</h1>
          <p className="mb-6 text-[15px]" style={{ color: AUTH_SUBTLE }}>This password reset link is invalid or has expired.</p>
          <Link to="/login"><PrimaryButton type="button">Back to Login</PrimaryButton></Link>
        </div>
      </AuthShell>
    );
  }

  if (isSuccess) {
    return (
      <AuthShell backTo={null}>
        <div className="text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: '#059669' }} />
          <h1 className="text-2xl mb-2">Password reset</h1>
          <p className="mb-6 text-[15px]" style={{ color: AUTH_SUBTLE }}>Your password has been reset successfully. You can now log in with your new password.</p>
          <Link to="/login"><PrimaryButton type="button">Go to Login</PrimaryButton></Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell backTo="/login" backLabel="Back to Login">
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#EEF2FB' }}>
          <Lock className="w-6 h-6" style={{ color: AUTH_BLUE }} />
        </div>
        <h1 className="text-2xl">Reset password</h1>
        <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>Enter your new password below</p>
      </div>
      <form onSubmit={handleResetPassword} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password" style={{ color: AUTH_LABEL }}>New Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: ICON }} />
            <Input id="password" type="password" placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="pl-10 h-12" required minLength={6} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" style={{ color: AUTH_LABEL }}>Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: ICON }} />
            <Input id="confirmPassword" type="password" placeholder="••••••••" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className="pl-10 h-12" required />
          </div>
        </div>
        <PrimaryButton type="submit" disabled={isLoading}>{isLoading ? 'Resetting...' : 'Reset Password'}</PrimaryButton>
      </form>
    </AuthShell>
  );
}

export default ResetPassword;
